import { privateApiClient } from './apiClient';
import { storageKeys } from '../config/environment';

/**
 * Service pour gérer les statistiques et graphiques du dashboard
 */

/**
 * Récupérer les statistiques du dashboard
 * @param {Object} params - Paramètres { own, srv, act, crc, pvd, sol }
 * @returns {Promise<Object>} Statistiques du dashboard
 */
export const fetchDashboardStats = async (params = {}) => {
    const defaultParams = {
        own: 'all',
        srv: 'all',
        act: 'all',
        crc: 'CDF',
        pvd: 'all',
        ...params
    };
    
    const { data } = await privateApiClient.get('/trs/transactions/dashboard', {
        params: defaultParams
    });
    
    // Retourner des valeurs par défaut si aucune donnée
    if (!data) {
        return {
            yearlyAmount: 0,
            lastYearAmount: 0,
            monthlyAmount: 0,
            lastMonthAmount: 0,
            dailyAmount: 0,
            lastDayAmount: 0
        };
    }
    
    return data;
};

/**
 * Montants encaissés attribués à un caissier (accountable TRS).
 * @param {Object} opts
 * @param {string} opts.orgId - ID organisation (own)
 * @param {string} opts.cashierId - ID caissier (act)
 * @param {string} [opts.currency='CDF']
 * @param {string} [opts.solutionId]
 * @returns {Promise<{dailyAmount:number,monthlyAmount:number,yearlyAmount:number}>}
 */
export const fetchCashierEncashmentStats = async ({
    orgId,
    cashierId,
    currency = 'CDF',
    solutionId,
} = {}) => {
    if (!orgId || !cashierId) {
        return { dailyAmount: 0, monthlyAmount: 0, yearlyAmount: 0 };
    }
    const sol =
        solutionId ||
        localStorage.getItem(storageKeys.solution) ||
        localStorage.getItem('netikash_solution') ||
        '';
    const data = await fetchDashboardStats({
        own: orgId,
        sol,
        act: cashierId,
        crc: currency,
        srv: 'all',
        pvd: 'all',
    });
    return {
        dailyAmount: Number(data?.dailyAmount) || 0,
        monthlyAmount: Number(data?.monthlyAmount) || 0,
        yearlyAmount: Number(data?.yearlyAmount) || 0,
    };
};

/**
 * Récupérer les données du graphique de transactions
 * @param {Object} params - Paramètres { own, sol, crc, yar }
 * @returns {Promise<Object>} Données du graphique
 */
export const fetchTransactionChart = async (params = {}) => {
    const defaultParams = {
        own: 'all',
        crc: 'CDF',
        yar: new Date().getFullYear(),
        ...params
    };
    
    const { data } = await privateApiClient.get('/trs/transactions/chart', {
        params: defaultParams
    });
    
    // Normaliser les données pour s'assurer que les valeurs null/undefined sont converties en 0
    if (data && data.datasets && Array.isArray(data.datasets)) {
        data.datasets = data.datasets.map((dataset) => ({
            ...dataset,
            data: dataset.data.map((d) => (d === null || d === undefined ? 0 : parseFloat(d)))
        }));
    }
    
    return data || {
        labels: [],
        datasets: []
    };
};

/**
 * Récupérer le solde du compte
 * @param {Object} params - Paramètres { sol, crc, own }
 * @returns {Promise<Object>} { balance: number, currency: string }
 */
export const fetchAccountBalance = async (params = {}) => {
    const defaultParams = {
        sol: '',
        crc: 'CDF',
        own: 'all',
        ...params
    };
    
    const { data } = await privateApiClient.get('/trs/accounts', {
        params: defaultParams
    });
    
    return {
        balance: data?.balance || 0,
        currency: data?.currency?.toUpperCase() || 'CDF'
    };
};

/**
 * Récupérer les détails du compte (solde, entrées, sorties, etc.)
 * @param {Object} params - Paramètres { sol, crc, own } (own = id organisation)
 * @returns {Promise<Object>} Compte complet
 */
export const fetchAccountDetails = async (params = {}) => {
    const defaultParams = {
        sol: '',
        crc: 'CDF',
        own: 'all',
        ...params
    };
    const { data } = await privateApiClient.get('/trs/accounts', {
        params: defaultParams
    });
    return data || { balance: 0, currency: 'CDF', incomingBalance: 0, outgoingBalance: 0 };
};











